import {
	useEffect,
	useId,
	useRef,
	type HTMLAttributes,
	type ReactNode,
} from "react";
import { cn } from "@/lib/cn";
import { IconButton } from "@/components/primitives/IconButton";
import { IconX } from "@/components/primitives/icons";

type DialogProps = {
	open: boolean;
	onClose: () => void;
	title: string;
	description?: string;
	children: ReactNode;
	className?: string;
	showClose?: boolean;
};

export function Dialog({
	open,
	onClose,
	title,
	description,
	children,
	className,
	showClose = true,
}: DialogProps) {
	const ref = useRef<HTMLDialogElement>(null);
	const titleId = useId();
	const descId = useId();

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (open) {
			if (!el.open) el.showModal();
		} else if (el.open) {
			el.close();
		}
	}, [open]);

	return (
		<dialog
			ref={ref}
			className={cn(
				"m-auto w-[calc(100%-2rem)] max-w-md rounded-[var(--radius-panel)] border-0 bg-transparent p-0 text-fg",
				"shadow-none open:flex open:flex-col",
				"[&::backdrop]:bg-zinc-950/50",
				className,
			)}
			aria-labelledby={titleId}
			aria-describedby={description ? descId : undefined}
			onClose={onClose}
			onClick={(e) => {
				if (e.target === ref.current) onClose();
			}}
		>
			<div
				className={cn(
					"rounded-[var(--radius-panel)] bg-surface p-5 text-fg",
					"shadow-[var(--shadow-pop)] ring-1 ring-[var(--ring-hairline)]",
				)}
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<h2 id={titleId} className="text-lg font-semibold tracking-tight">
							{title}
						</h2>
						{description ? (
							<p id={descId} className="mt-1 text-sm text-fg-muted">
								{description}
							</p>
						) : null}
					</div>
					{showClose ? (
						<IconButton label="Schließen" onClick={onClose}>
							<IconX />
						</IconButton>
					) : null}
				</div>
				{children}
			</div>
		</dialog>
	);
}

type DialogActionsProps = HTMLAttributes<HTMLDivElement>;

export function DialogActions({ className, ...rest }: DialogActionsProps) {
	return (
		<div
			className={cn("mt-5 flex flex-wrap justify-end gap-2", className)}
			{...rest}
		/>
	);
}
